import { useEffect, useMemo, useState } from 'react'
import { client } from '../lib/api'
import { CONNECTION_INTENT_LABEL, outreachLabel, placeholderGradient } from '../lib/ui'
import { ProfileSheet } from './ProfileSheet'
import type { PublicProfile } from '../../shared/types'

export function Search({
  onOpenChat,
  notify,
  adHtml,
}: {
  onOpenChat: (p: PublicProfile) => void
  notify: (msg: string) => void
  adHtml: string | null
}) {
  const [profiles, setProfiles] = useState<PublicProfile[] | null>(null)
  const [query, setQuery] = useState('')
  const [photoMap, setPhotoMap] = useState<Record<string, string | null>>({})
  const [sheetId, setSheetId] = useState<string | null>(null)

  useEffect(() => {
    client
      .feed()
      .then((r) => {
        const seen = new Set<string>()
        setProfiles(r.profiles.filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true))))
      })
      .catch((e) => {
        notify((e as Error).message)
        setProfiles([])
      })
  }, [notify])

  useEffect(() => {
    if (!profiles?.length) return
    void (async () => {
      const next: Record<string, string | null> = { ...photoMap }
      for (const p of profiles) {
        if (next[p.id] !== undefined) continue
        try {
          const d = await client.profileDetail(p.id)
          next[p.id] = d.photos.find((ph) => ph.urlPath && ph.kind === 'public')?.urlPath ?? null
        } catch {
          next[p.id] = null
        }
      }
      setPhotoMap(next)
    })()
  }, [profiles]) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!profiles) return null
    const q = query.trim().toLowerCase()
    if (!q) return profiles
    return profiles.filter(
      (p) =>
        p.displayName.toLowerCase().includes(q) ||
        (p.city ?? '').toLowerCase().includes(q) ||
        p.lifestyleTags.some((t) => t.toLowerCase().includes(q)),
    )
  }, [profiles, query])

  return (
    <div className="panel-page">
      <span className="eyebrow">Browse</span>
      <h1>Search</h1>

      <div className="search-toolbar">
        <input
          className="input"
          placeholder="Name, city, or interest…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {filtered === null ? (
        <div className="loading-center">
          <div className="spinner" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <div>🔍</div>
          <h2>No results</h2>
          <p>Try a different search or widen your filters.</p>
        </div>
      ) : (
        <div className="search-grid">
          {filtered.map((p, i) => (
            <SearchTile
              key={p.id}
              profile={p}
              photoUrl={photoMap[p.id] ?? null}
              onClick={() => setSheetId(p.id)}
              showAdAfter={adHtml != null && i === 5}
              adHtml={adHtml}
            />
          ))}
        </div>
      )}

      <ProfileSheet
        profileId={sheetId}
        onClose={() => setSheetId(null)}
        notify={notify}
        showActions
        onMessage={(profile) => {
          setSheetId(null)
          onOpenChat(profile)
        }}
        onLike={async (profile) => {
          try {
            const res = await client.like(profile.id)
            notify(res.matched ? "It's a match!" : 'Like sent')
          } catch (e) {
            notify((e as Error).message)
          }
        }}
      />
    </div>
  )
}

function SearchTile({
  profile,
  photoUrl,
  onClick,
  showAdAfter,
  adHtml,
}: {
  profile: PublicProfile
  photoUrl: string | null
  onClick: () => void
  showAdAfter: boolean
  adHtml: string | null
}) {
  return (
    <>
      <button type="button" className="grid-tile" onClick={onClick}>
        {photoUrl ? (
          <img src={photoUrl} alt="" className="grid-tile-bg" />
        ) : (
          <div className="grid-tile-bg" style={{ background: placeholderGradient(profile.accentColor) }} aria-hidden>
            {profile.displayName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="tile-overlay">
          <strong>
            {profile.displayName}
            {profile.age != null ? `, ${profile.age}` : ''}
          </strong>
          <small>
            {outreachLabel(profile.outreachPreference)} · {CONNECTION_INTENT_LABEL[profile.connectionIntent]}
            {profile.city ? ` · ${profile.city}` : ''}
          </small>
        </div>
      </button>
      {showAdAfter && adHtml && (
        <div className="ad-slot" role="complementary">
          <div dangerouslySetInnerHTML={{ __html: adHtml }} />
        </div>
      )}
    </>
  )
}
